/**
 * Promo domain — promotions list + claim flow.
 *
 * The svc-promo API serves both the legacy Promotion shape (used by the
 * existing test suite) and the v2 `claimPromotionV2` flow. We model both
 * so the fields we need at render time are present without breaking the
 * established contract.
 */
import type { QueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api/client.js';
import { useAuthStore } from '../../stores/auth.js';

export const PROMO_QUERY_KEYS = {
  list: (params?: { status?: string; kind?: string }) =>
    ['promo', 'list', params ?? {}] as const,
  claims: (params?: { status?: string }) =>
    ['promo', 'claims', params ?? {}] as const,
  myClaims: (params?: { status?: string }) =>
    ['promo', 'my-claims', params ?? {}] as const,
  /** Prefix that matches every `myClaims(...)` variant — use for invalidation. */
  myClaimsRoot: ['promo', 'my-claims'] as const,
  /**
   * The providers / game types the caller's live bonuses allow (#434).
   *
   * It lives here rather than beside its own module because it is part of the
   * claim family: the answer changes the instant a claim is created or ends, and
   * `invalidateClaimState` below is what keeps it and `myClaims` moving together.
   */
  playRestrictions: ['promo', 'play-restrictions'] as const,
  /**
   * Prefix that matches every `rebatePreview(...)` variant. Claiming one rebate
   * spends the period for the sibling rebate promotion too, so a claim
   * invalidates the whole family rather than the promotion it named.
   */
  rebatePreviewRoot: ['promo', 'rebate-preview'] as const,
  rebatePreview: (promotionId: string) => ['promo', 'rebate-preview', promotionId] as const,
  checkinStreak: (promotionId: string) => ['promo', 'checkin-streak', promotionId] as const,
} as const;

// ── Promotion ─────────────────────────────────────────────────────────────────

/**
 * Promotion as served by GET /promotions.
 *
 * Legacy fields (`title`, `bonusType`, `bonusValue`, `minDeposit`, `maxBonus`,
 * `endsAt`, etc.) are retained because existing tests assert on them. The
 * DB-aligned fields (`name`, `bannerUrl`, `conditions`, `rewardRules`,
 * `periodTo`, `kind`) are what the real service returns today.
 *
 * Renderers should prefer real fields and fall back to legacy.
 */
export interface Promotion {
  id: string;
  agentId: string | null;
  // DB-aligned
  code?: string;
  name?: string;
  description: string | null;
  bannerUrl?: string | null;
  kind: string;
  applyOn?: string;
  audience?: string;
  conditions?: Record<string, unknown> | null;
  rewardRules?: Record<string, unknown> | null;
  turnoverMultiplier: number | string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  // `maxClaimsTotal` is deliberately absent: the client audience is not sent the
  // campaign's total quota or the counts measured against it, because the
  // promotions list answers before login and how many slots a capped campaign
  // has left is an operator figure (#453). `maxClaimsPerMember` is the caller's
  // own allowance and is still served.
  maxClaimsPerMember?: number | null;
  autoApprove?: boolean;
  expiryDays?: number | null;
  status: 'active' | 'inactive' | 'archived';
  createdAt: string;
  /**
   * The server's verdict on whether THIS member may start a NEW claim on the
   * promotion, for the kinds whose eligibility is a fact about the member rather
   * than about the campaign — today only `first_deposit`, which is spent the
   * moment the member's first deposit is approved (#149).
   *
   * It says nothing about a claim they already hold: the verdict flips at the
   * same moment a reservation is about to become a live bonus, so a promotion
   * with a live claim on it stays on the list either way — see
   * `isPromoStillOffered`.
   *
   * Absent means the server did not evaluate it: an operator list, an anonymous
   * browse, a kind with no such gate, an older build. The card must stay in every
   * one of those cases; only `false` hides it.
   */
  memberEligible?: boolean;

  // Legacy fields kept for compatibility with existing tests/fixtures
  title?: string;
  gameType?: string | null;
  bonusType?: string;
  bonusValue?: number;
  minDeposit?: number | null;
  maxBonus?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
}

export interface PromotionPage {
  items: Promotion[];
  total: number;
  page: number;
  pageSize: number;
}

export function fetchPromotions(params?: {
  status?: string;
  kind?: string;
  pageSize?: number;
}): Promise<PromotionPage> {
  const qs = new URLSearchParams();
  qs.set('status', params?.status ?? 'active');
  if (params?.kind) qs.set('kind', params.kind);
  qs.set('pageSize', String(params?.pageSize ?? 50));
  return apiFetch<PromotionPage>(`/promotions?${qs.toString()}`);
}

/**
 * Refresh everything that depends on WHICH claims the member currently holds.
 *
 * One function rather than a pair of `invalidateQueries` calls at each site,
 * because the two keys have to move together and there are six sites. A claim
 * created (claimed, coupon redeemed, spin won) or ended (turnover completed by a
 * bet) changes both the claim list AND the play restriction it imposes — and the
 * restriction is the one that decides whether the lobby locks a card, so a site
 * that refreshes only the list leaves the player looking at cards the server
 * will refuse. A test asserts no site invalidates the claim keys directly.
 *
 * Callers add their own extras (wallet balances, rebate previews, the promotions
 * list); this covers only the claim family.
 */
export function invalidateClaimState(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: PROMO_QUERY_KEYS.myClaimsRoot });
  void qc.invalidateQueries({ queryKey: PROMO_QUERY_KEYS.playRestrictions });
}

// ── Promotion display helpers ────────────────────────────────────────────────

/** Best-effort title resolution (DB column is `name`; older shape used `title`). */
export function promoTitle(p: Promotion): string {
  return p.name ?? p.title ?? p.code ?? 'Promotion';
}

/** Effective end date — DB exposes `periodTo`, legacy shape used `endsAt`. */
export function promoEndsAt(p: Promotion): string | null {
  return p.periodTo ?? p.endsAt ?? null;
}

/**
 * Past its end date, so the claim guard would reject it with PROMO-010. The
 * list endpoint already drops these for players; this covers a card that was
 * fetched while still live and has since aged out in an open tab.
 * An open-ended promotion (no end date) is never expired.
 */
export function isPromoExpired(p: Promotion, nowMs: number = Date.now()): boolean {
  const endsAt = promoEndsAt(p);
  if (!endsAt) return false;
  const end = new Date(endsAt).getTime();
  return Number.isFinite(end) && end < nowMs;
}

/** Effective banner — only set if backend provides one. */
export function promoBanner(p: Promotion): string | null {
  return p.bannerUrl ?? null;
}

// ── Promotion Claims ──────────────────────────────────────────────────────────

/**
 * PromotionClaim — combines the legacy shape (status: pending/approved/…)
 * with v2 fields (grantedAmount/turnoverRequired/turnoverAchieved/expiresAt,
 * status: active/completed/forfeited/…).
 */
export interface PromotionClaim {
  id: string;
  /** Null for a bonus an operator granted outside any campaign (#296). */
  promotionId: string | null;
  memberId: string;
  agentId?: string | null;
  status: string;
  // v2 fields
  grantedAmount?: number | string | null;
  turnoverRequired?: number | string | null;
  turnoverAchieved?: number | string | null;
  expiresAt?: string | null;
  forfeitedAmount?: number | string | null;
  idempotencyKey?: string | null;
  /**
   * True only for a claim inserted 'pending' because the promotion needs an
   * operator's approval — nothing has been credited and no deposit upgrades it.
   * The one field that tells that apart from a deposit reservation.
   */
  awaitingCredit?: boolean;
  /**
   * Which wallet pool the principal is in — 'promotion' for a bonus, 'cashback'
   * for a turnover-locked rebate credit. Optional for the reason
   * `promotionKind` is: a bundle can reach a server that does not send it yet,
   * and absent must read as the pool every claim used to be in.
   */
  pool?: string | null;
  /** The wallet transaction that paid this claim; null while nothing has moved. */
  txId?: string | null;
  // Legacy fields
  amount?: number | null;
  currency?: string | null;
  depositId?: string | null;
  claimedAt?: string;
  processedAt?: string | null;
  promotionTitle?: string;
  /**
   * The parent campaign's `kind` and `status`, joined on by
   * `listClaimsByMember`. Both optional because this client and svc-promo
   * deploy on independent pipelines: a client that ships first talks to a
   * server that does not send them yet, so absent must mean "not known" and
   * never "not eligible".
   */
  promotionKind?: string | null;
  promotionStatus?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ClaimPage {
  items: PromotionClaim[];
  total: number;
  page: number;
  pageSize: number;
}

export function fetchPromotionClaims(params?: {
  status?: string;
  pageSize?: number;
}): Promise<ClaimPage> {
  const qs = new URLSearchParams();
  const memberId = useAuthStore.getState().subject?.id;
  if (memberId) qs.set('memberId', memberId);
  if (params?.status) qs.set('status', params.status);
  qs.set('pageSize', String(params?.pageSize ?? 20));
  return apiFetch<ClaimPage>(`/promotion-claims?${qs.toString()}`);
}

/** Authenticated /me listing — preferred over the legacy memberId query. */
export function fetchMyClaims(params?: {
  status?: 'active' | 'completed' | 'forfeited' | 'rejected' | 'pending';
  /** Server caps responses (default 100, max 200) — pass a smaller bound when
   *  the caller only needs a recent window. */
  limit?: number;
}): Promise<{ items: PromotionClaim[] }> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<{ items: PromotionClaim[] }>(`/promotion-claims/me${suffix}`);
}

/** True when any claim is still working off turnover (active + not yet met) —
 *  drives the bounded, visible-only poll on the promo page. */
export function hasInProgressClaim(items: PromotionClaim[] | undefined): boolean {
  if (!items) return false;
  return items.some(
    (c) => c.status === 'active' && bigIntish(c.turnoverAchieved) < bigIntish(c.turnoverRequired),
  );
}

export function fetchMyClaim(id: string): Promise<PromotionClaim> {
  return apiFetch<PromotionClaim>(`/promotion-claims/me/${id}`);
}

// ── Claim mutations ──────────────────────────────────────────────────────────

export interface ClaimPromotionArgs {
  promotionId: string;
  depositId?: string;
}

/**
 * Legacy claim endpoint with no caller outside its own test. The route it posts
 * to is operator-only, so a player token — the only kind this app holds — gets a
 * 403 from it; every player claim goes through the v2 dispatch instead. Kept
 * exported for the existing test suite.
 *
 * The grant amount is computed server-side from the promotion config; the body
 * only identifies the member (forced from the token) and an optional depositId.
 */
export function claimPromotion(args: ClaimPromotionArgs): Promise<PromotionClaim> {
  const memberId = useAuthStore.getState().subject?.id;
  if (!memberId) return Promise.reject(new Error('not authenticated'));
  return apiFetch<PromotionClaim>(`/promotions/${args.promotionId}/claims`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      memberId,
      ...(args.depositId ? { depositId: args.depositId } : {}),
    }),
  });
}

export interface ClaimPromotionV2Args {
  promotionId: string;
  idempotencyKey?: string;
}

/**
 * Player self-service claim — uses the v2 dispatcher which evaluates eligibility
 * by `promotion.kind` and credits the bonus pool when applicable.
 *
 * The deposit amount + sequence are resolved server-side (deposit-approved
 * consumer / finance records), so the body carries nothing the player controls.
 * Idempotency-Key is forwarded so retries dedupe deterministically.
 */
export function claimPromotionV2(args: ClaimPromotionV2Args): Promise<PromotionClaim> {
  const memberId = useAuthStore.getState().subject?.id;
  if (!memberId) return Promise.reject(new Error('not authenticated'));
  const idemKey = args.idempotencyKey ?? `claim:${args.promotionId}:${memberId}`;
  return apiFetch<PromotionClaim>(`/promotions/${args.promotionId}/claim`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'idempotency-key': idemKey,
    },
    body: JSON.stringify({}),
  });
}

// ── Coupon redemption ────────────────────────────────────────────────────────

export interface Coupon {
  id: string;
  code: string;
  promotionId?: string | null;
  status?: string;
}

export interface RedeemCouponResult {
  coupon: Coupon;
  claim: PromotionClaim;
}

/**
 * Redeem a coupon code for the authenticated player.
 *
 * The member id is forced server-side from the JWT — the body carries only the
 * code. Resolves to gateway `/api/client/coupons/redeem` (apiFetch prepends the
 * `VITE_API_BASE_URL` audience prefix, identical to the claim flow above).
 */
export function redeemCoupon(args: { code: string }): Promise<RedeemCouponResult> {
  return apiFetch<RedeemCouponResult>(`/coupons/redeem`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ code: args.code }),
  });
}

// ── Selectors ────────────────────────────────────────────────────────────────

/** Which wallet pool a claim's principal is in. Mirrors `promo.promotion_claims.pool`. */
export type ClaimPool = 'promotion' | 'cashback';

/**
 * The pool a claim belongs to, with the same default svc-promo's column has.
 *
 * Absent means "a server that predates the column", and every claim such a
 * server ever wrote was a promotion-pool one — the two SPAs and svc-promo ship
 * on independent pipelines, so a bundle that lands first must not read a
 * missing field as a different pool.
 */
export function claimPool(c: PromotionClaim): ClaimPool {
  return c.pool === 'cashback' ? 'cashback' : 'promotion';
}

/**
 * Pick the first claim of `pool` still owing turnover.
 *
 * Pool-scoped since cashback credits can be turnover-locked too: the
 * wallet page's bonus banner and its cashback tile ask the same question of
 * different money, and a single unscoped answer would have made the bonus
 * banner light up for a locked rebate credit — and the cashback tile hold back
 * the pool for a locked bonus that is not in it.
 *
 * DISPLAY only. The refusal that actually holds the money is svc-wallet's
 * (`WALLET-PA-004` / `WALLET-PA-007`) against svc-promo's own figure; this is
 * the same predicate reached through a list the browser happens to hold, so it
 * may be staler and must never be the thing a transfer is allowed on.
 */
export function findActiveClaim(
  claims: PromotionClaim[],
  pool: ClaimPool = 'promotion',
): PromotionClaim | null {
  for (const c of claims) {
    if (c.status !== 'active') continue;
    if (claimPool(c) !== pool) continue;
    const required = bigIntish(c.turnoverRequired);
    const achieved = bigIntish(c.turnoverAchieved);
    if (required > 0n && achieved < required) return c;
  }
  return null;
}

/**
 * Principal in `pool` that still owes turnover, summed over every live claim.
 *
 * `findActiveClaim` names ONE claim, which is all a progress bar needs; a
 * "convertible now" figure needs the whole sum, because a member holding two
 * locked cashback credits has both held back.
 *
 * Deliberately NOT conservative in every direction, and the gap is worth
 * naming. A claim whose `pool` the server did not send counts as promotion, so
 * a bundle talking to an older svc-promo holds back LESS cashback than the
 * server does — and a list that is merely stale (a rebate claimed in another
 * tab, a credit that landed since the last poll) is short of live locks the
 * same way. Either shape makes this figure OVER-offer, and the modal then
 * presents an amount `POST /me/convert-cashback` answers `WALLET-PA-007` to.
 * That is a wasted tap, not a leak: the server re-derives the whole figure from
 * svc-promo under the wallet row lock and is the only thing that decides what
 * moves. This exists to save the tap, never to authorise the transfer.
 */
export function lockedMinorFor(claims: PromotionClaim[], pool: ClaimPool): bigint {
  let total = 0n;
  for (const c of claims) {
    if (c.status !== 'active') continue;
    if (claimPool(c) !== pool) continue;
    const required = bigIntish(c.turnoverRequired);
    const achieved = bigIntish(c.turnoverAchieved);
    if (required > 0n && achieved < required) total += bigIntish(c.grantedAmount);
  }
  return total;
}

/**
 * WAGERING still owed in `pool`, as opposed to the principal `lockedMinorFor`
 * holds back.
 *
 * The two are different money and a multiplier of 2 makes the second twice the
 * first, so a player told the principal is told to bet half of what the gate is
 * waiting on. This is the figure that answers "what do I have to DO"; the
 * principal answers "what is being held".
 *
 * Same staleness contract as its sibling: display only, and never the thing a
 * transfer is allowed on.
 */
export function turnoverRemainingMinorFor(claims: PromotionClaim[], pool: ClaimPool): bigint {
  let total = 0n;
  for (const c of claims) {
    if (c.status !== 'active') continue;
    if (claimPool(c) !== pool) continue;
    const required = bigIntish(c.turnoverRequired);
    const achieved = bigIntish(c.turnoverAchieved);
    if (required > 0n && achieved < required) total += required - achieved;
  }
  return total;
}

/**
 * The part of a pool the player may move right now: balance minus locked
 * principal, floored at zero.
 *
 * The browser's copy of `sweepableBonus` (`apps/wallet/src/repo/sweepable-bonus.ts`)
 * minus its credited-since term, which only the server can measure. That makes
 * this figure equal to or LARGER than the server's only when the claim list is
 * stale in the player's favour — so the caller pairs it with the server's
 * refusal rather than trusting it, and the floor keeps a pool smaller than its
 * locked principal reading as "nothing free" instead of a negative offer.
 */
export function freePoolMinor(poolMinor: number | bigint, lockedMinor: bigint): bigint {
  const pool = typeof poolMinor === 'bigint' ? poolMinor : bigIntish(poolMinor);
  if (pool <= 0n) return 0n;
  return pool > lockedMinor ? pool - lockedMinor : 0n;
}

/** Tolerant bigint coercion — payload may arrive as number, string, or null. */
export function bigIntish(v: number | string | null | undefined): bigint {
  if (v == null) return 0n;
  if (typeof v === 'bigint') return v;
  if (typeof v === 'number') return BigInt(Math.round(v));
  try {
    return BigInt(v);
  } catch {
    return 0n;
  }
}

/** Turnover progress as a 0..1 fraction. */
export function turnoverProgress(claim: PromotionClaim): number {
  const req = Number(bigIntish(claim.turnoverRequired));
  if (!req) return 0;
  const ach = Number(bigIntish(claim.turnoverAchieved));
  if (ach <= 0) return 0;
  if (ach >= req) return 1;
  return ach / req;
}

// ── Check-in Streak ───────────────────────────────────────────────────────────

/** A gate an operator can put on a promotion, plus the "nobody could tell" case. */
export type CheckinGate =
  | 'minTotalDeposit'
  | 'minTotalTurnover'
  | 'minDepositCount'
  /**
   * The promotion's money gates are set in a currency this account does not
   * hold, so no figure of the member's is on the same scale as the bar.
   * `required` is then the CODE the promotion names and `current` the member's
   * own, or `'unknown'` where nobody could read it.
   */
  | 'currency'
  | 'unknown';

/**
 * One requirement the member has not met yet.
 *
 * `required` and `current` are DIGIT STRINGS, and their unit depends on the
 * gate: the two money gates carry minor units, which only mean anything through
 * `formatMoney` with the brand's currency; `minDepositCount` is a plain count
 * and must never reach the money formatter (#426). Both are null on `unknown`,
 * where there is no number anybody measured.
 */
export interface CheckinUnmetGate {
  gate: CheckinGate;
  required: string | null;
  current: string | null;
}

/** What the card needs to name the requirements before the player presses. */
export interface CheckinEligibility {
  conditions?: {
    minTotalDeposit?: string;
    minTotalTurnover?: string;
    minDepositCount?: number;
    /**
     * The currency the two money bars above are minor units of, on a campaign
     * that names one — a platform campaign always does (#426). Absent means the
     * bars are the member's own brand currency, which is every brand campaign.
     *
     * The card formats a bar WITH IT where it is present: a JPY campaign's
     * 100000 read at the viewer's own exponent is the same hundredfold this
     * field exists to stop, moved from the gate to the label.
     */
    currency?: string;
  };
  progress?: { totalDeposit: string; totalTurnover: string; depositCount: number } | null;
  unmet: CheckinUnmetGate[];
  eligible: boolean;
}

export interface CheckinStreak {
  promotionId: string;
  currentDay: number;
  daysClaimed: number[];
  /**
   * The reward on `currentDay` — the day the ledger is pricing right now. Once
   * today has been claimed this is the amount that was just paid, not the next
   * one; `amountNextDay` is what the "back tomorrow" copy needs.
   */
  amountToday: string;
  /**
   * The reward on the day after `currentDay`. Optional because this client and
   * svc-promo deploy on independent pipelines: a client that ships first talks
   * to a server that does not send it yet, and the card has to degrade to a
   * stale-but-plausible figure rather than to zero.
   */
  amountNextDay?: string;
  /**
   * Whether the member has already checked in on the current Asia/Bangkok day.
   * The only correct source for that question — a claim row cannot answer it
   * (see `nextActionFor`), and a client-side date comparison answers it in the
   * wrong timezone. Optional for the same deploy-skew reason as `amountNextDay`;
   * absent means "not known", and the card then offers no action.
   */
  claimedToday?: boolean;
  schedule: string[];
  /**
   * The eligibility gates the claim will be measured against, or null when the
   * promotion gates nobody.
   *
   * Optional for the same deploy-skew reason as the fields above, and the
   * difference matters more here: a client that ships before svc-promo must
   * treat an ABSENT field as "no gate known" and leave the claim live, not as a
   * refusal. Only an explicit `eligible: false` darkens the button.
   */
  eligibility?: CheckinEligibility | null;
}

export function fetchCheckinStreak(promotionId: string): Promise<CheckinStreak> {
  return apiFetch<CheckinStreak>(
    `/promotion-claims/checkin-streak?promotionId=${encodeURIComponent(promotionId)}`,
  );
}

// ── Rebate Preview (turnover_rebate + cashback) ───────────────────────────────

export interface RebateBreakdownEntry {
  providerId: string;
  gameType: string;
  bips: number;
  baseMinor: string;
  amountMinor: string;
}

/**
 * The reasons `GET /promotion-claims/rebate-preview` can give for withholding a
 * claim (#447). Mirrors `REBATE_BLOCKED_REASONS` in
 * `apps/promo/src/repo/rebate.ts`. A reason the server adds later arrives here
 * as a value outside this union: `rebateCaptionKey` looks it up in a table and
 * falls through to the flag-based copy when it misses, so the card degrades to
 * its previous sentence rather than rendering an untranslated key.
 */
export type RebateBlockedReason =
  | 'claimed_by_sibling'
  | 'already_claimed'
  | 'below_minimum'
  | 'rates_changed'
  | 'claim_window_closed'
  | 'period_open'
  | 'no_play';

export interface RebatePreview {
  promotionId: string;
  periodKey: string;
  windowFrom: string;
  windowTo: string;
  basis: 'net_loss' | 'turnover';
  amountMinor: string;
  breakdown: { basis: string; totalBaseMinor: string; entries: RebateBreakdownEntry[]; capped: boolean };
  alreadyClaimed: boolean;
  claimable: boolean;
  /** False while `periodKey` is still running — `amountMinor` is the accrual so far. */
  periodClosed?: boolean;
  /** Set when the sibling rebate promotion took this period instead. */
  blockedBy?: string | null;
  /**
   * Why the preview is not offering a claim, or null when it is (#447).
   * Optional because an older svc-promo does not send it — `rebateCaptionKey`
   * falls back to the flags it used before when the field is absent.
   */
  blockedReason?: RebateBlockedReason | null;
}

/** Accrued rebate for the current claimable period — used by the cashback +
 *  turnover_rebate cards. Computes from bet winloss-hourly; does not credit. */
export function fetchRebatePreview(promotionId: string): Promise<RebatePreview> {
  return apiFetch<RebatePreview>(
    `/promotion-claims/rebate-preview?promotionId=${encodeURIComponent(promotionId)}`,
  );
}
